import {Children, isValidElement} from 'react'
import type {ReactNode} from 'react'
import type {DocsArticle, DocsSection} from './types'

export interface ArticleEntry {
  article: DocsArticle
  sectionTrail: DocsSection[]
  path: string
}

export interface SectionEntry {
  section: DocsSection
  trail: DocsSection[]
}

export interface HeadingItem {
  id: string
  text: string
  level: number
}

export const sortByDisplayOrder = <T extends {displayOrder: number; title: string}>(items: T[]) => {
  return [...items].sort((left, right) => {
    if (left.displayOrder !== right.displayOrder) {
      return left.displayOrder - right.displayOrder
    }

    return left.title.localeCompare(right.title)
  })
}

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[`*_~]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')

export const extractText = (children: ReactNode): string => {
  return Children.toArray(children)
    .map(child => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child)
      }

      if (isValidElement<{children?: ReactNode}>(child)) {
        return extractText(child.props.children)
      }

      return ''
    })
    .join('')
}

export const extractMarkdownHeadings = (markdown: string): HeadingItem[] => {
  const slugCounts = new Map<string, number>()

  return markdown
    .split('\n')
    .map(line => line.match(/^(#{2,3})\s{1,4}(\S.*)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map(match => {
      const level = match[1].length
      const text = match[2].replace(/[`*_~]/g, '').trim()
      const baseId = slugify(text)
      const count = slugCounts.get(baseId) ?? 0
      slugCounts.set(baseId, count + 1)

      return {
        id: count === 0 ? baseId : `${baseId}-${count + 1}`,
        text,
        level,
      }
    })
}

export const normalizeMarkdownContent = (markdown: string): string => {
  return markdown.replace(/\\`/g, '`').replace(/\r\n/g, '\n')
}

export const buildArticleEntries = (sections: DocsSection[], parentTrail: DocsSection[] = []): ArticleEntry[] => {
  const entries: ArticleEntry[] = []

  sortByDisplayOrder(sections).forEach(section => {
    const trail = [...parentTrail, section]

    sortByDisplayOrder(section.articles).forEach(article => {
      entries.push({
        article,
        sectionTrail: trail,
        path: `/docs/${trail.map(item => item.slug).join('/')}/${article.slug}`,
      })
    })

    entries.push(...buildArticleEntries(section.children, trail))
  })

  return entries
}

export const buildSectionEntries = (sections: DocsSection[], parentTrail: DocsSection[] = []): SectionEntry[] => {
  const entries: SectionEntry[] = []

  sortByDisplayOrder(sections).forEach(section => {
    const trail = [...parentTrail, section]
    entries.push({section, trail})
    entries.push(...buildSectionEntries(section.children, trail))
  })

  return entries
}

export const collectExpandableSections = (sections: DocsSection[]): string[] => {
  return sections.flatMap(section => [section.id, ...collectExpandableSections(section.children)])
}

export const scrollToHeading = (headingId: string) => {
  const target = document.getElementById(headingId)
  if (!target) {
    return
  }

  target.scrollIntoView({behavior: 'smooth', block: 'start'})

  if (window.location.hash !== `#${headingId}`) {
    window.history.replaceState(null, '', `#${headingId}`)
  }
}
